import type { TEntityId } from "./entity-id";

// NOTE: NSAI-5680 — where a fact came from, resolved server-side for the
// settings list so the user can trace a memory back to the conversation that
// produced it. Present only when the source chat still exists and belongs to
// the user; the message part is present only when the fact was traced to one.
export interface IUserMemoryFactSourceView {
  chatUuid: string;
  // NOTE: Null when the chat has no title — render the "Untitled chat" label.
  chatTitle: string | null;
  // NOTE: Full ISO timestamp of when the fact was stated (the source message's
  // time, or the chat's when only the chat is known). Rendered in the viewer's
  // timezone, like the fact's own createdAt — a date-only value would be
  // truncated in UTC and could show a different day than the "Added" line.
  statedAt: string;
  // NOTE: Who wrote the quoted message — compare against
  // USER_MEMORY_SOURCE_ROLE_USER; null when only the chat is known.
  messageRole: string | null;
  // NOTE: Server-bounded excerpt of the source message; null when unknown.
  messageExcerpt: string | null;
}

export interface IUserMemoryFactView {
  uuid: string;
  factType: string;
  // NOTE: "extracted" | "explicit" — lets the UI badge behavior facts vs items
  // the user explicitly saved. Optional for back-compat with older payloads.
  origin?: string;
  content: string;
  topic: string | null;
  confidence: number;
  sourceChatId: TEntityId | null;
  // NOTE: ISO timestamp strings over the wire (the backend serializes Date).
  createdAt: string;
  reinforcedAt: string | null;
  // NOTE: NSAI-5680 — resolved provenance. Optional for back-compat with older
  // server payloads; null when the fact has no traceable source. Last, per the
  // "optional props last" convention.
  source?: IUserMemoryFactSourceView | null;
}

// NOTE: NSAI-4244 — createFact's response so the UI can distinguish a
// genuinely new fact from one that matched an existing explicit fact and was
// only reinforced, and show the right toast for each.
export interface IUserMemoryFactCreateResult extends IUserMemoryFactView {
  wasDuplicate: boolean;
}

export interface IUserMemoryOverview {
  profile: string | null;
  facts: IUserMemoryFactView[];
  // NOTE: Total ACTIVE facts for the user, independent of the requested page —
  // drives the page-number pagination control (NSAI-4239). Optional for
  // back-compat with older server payloads that predate pagination.
  total?: number;
  // NOTE: NSAI-6370 — how many memories the user may save and how many they
  // already have. The cap is a deployment setting, so a client must render
  // whatever the server reports and never assume 100. Both optional for
  // back-compat: an older server omits them, and the UI then shows no counter
  // rather than "0 of 0".
  explicitLimit?: number;
  // NOTE: Usage in the scope this overview's saves go to — the personal space
  // for the settings read. It CAN exceed explicitLimit, after a deployment
  // lowers the cap: nothing is evicted, existing memories keep applying, only
  // new saves are refused. Render that state rather than assuming used <= limit.
  explicitUsed?: number;
}

// NOTE: NSAI-6751 — a fact's zero-based index in the unfiltered settings list,
// so a global search deep link can open the page that holds it.
export interface IUserMemoryFactPositionView {
  index: number;
}

// NOTE: NSAI-6371 — outcome of one line in a bulk import. Mirrors the server
// enum; kept as string unions rather than TS enums so both apps can compare
// them against a plain response body without importing a runtime value.
export type TUserMemoryImportStatus = "created" | "duplicate" | "skipped";

export type TUserMemoryImportSkipReason =
  | "empty"
  | "too_long"
  | "blocked_content"
  | "limit_reached"
  // NOTE: Client-only — the server never sends it. A batch after the first
  // failed, so the line was never submitted (buildUnsentMemoryImportResult).
  | "not_sent";

export interface IUserMemoryImportItemResult {
  // NOTE: Index in the submitted array, so the UI can point at the line the
  // user actually typed instead of reporting a bare count.
  index: number;
  content: string;
  status: TUserMemoryImportStatus;
  reason?: TUserMemoryImportSkipReason;
}

export interface IUserMemoryImportResult {
  // NOTE: True when nothing was stored — the caller asked what WOULD happen.
  // The preview reads this so it can never present itself as a committed
  // import.
  dryRun: boolean;
  created: number;
  duplicates: number;
  skipped: number;
  items: IUserMemoryImportItemResult[];
  // NOTE: Usage after the import, so the counter can be refreshed from this
  // same response instead of racing a follow-up read.
  explicitLimit: number;
  explicitUsed: number;
}

export interface IImportUserMemoryFactsPayload {
  contents: string[];
  // NOTE: Classify only, store nothing — what the preview asks for. The server
  // is the only side that can say whether a line duplicates something already
  // saved (content is encrypted at rest) or trips the blocked-data guardrail,
  // so the preview asks it rather than guessing.
  dryRun?: boolean;
}

// NOTE: Pagination params for the settings facts list (NSAI-4239). Both are
// optional; omitting them lets the server apply its own defaults.
export interface IGetUserMemoryOverviewParams {
  limit?: number;
  offset?: number;
  // NOTE: NSAI-4238 — free-text search over fact content and/or a fact-type
  // filter. Both optional; omitting them returns the unfiltered list.
  search?: string;
  factType?: string;
}

export interface IUpdateUserMemoryFactPayload {
  content: string;
}

// NOTE: Body for the create endpoint (POST /user/memory/facts). Saves an
// explicit, user-authored memory item (NSAI-4111 add-item UI → the create path
// added in NSAI-4109). `factType` is optional; the backend defaults it.
export interface ICreateUserMemoryFactPayload {
  content: string;
  factType?: string;
}

// NOTE: Body for the profile-correction endpoint (PATCH /user/memory/profile) —
// NSAI-4111. Replaces the narrative shown in settings.
export interface IUpdateUserMemoryProfilePayload {
  profile: string;
}

// NOTE: Response of the profile-correction endpoint — the saved narrative.
export interface IUserMemoryProfileView {
  profile: string;
}

// NOTE: NSAI-6369 — what memory must never store, as the settings screens read
// it. `own` is what this user wrote and can edit; `inherited` is what their
// organization set for everyone, which they can see but not change. Both are
// returned so the UI can show the whole effective list while keeping the two
// visibly distinct — an exclusion someone did not write should never look like
// one they did.
export interface IUserMemoryExclusionsView {
  own: string[];
  inherited: string[];
}

export interface IUpdateUserMemoryExclusionsPayload {
  categories: string[];
}

// NOTE: The organization-level list, for the workspace settings screen. Separate
// from the view above because an admin editing the policy is editing `own` for
// the organization, and conflating the two would make "can I edit this?" depend
// on which screen the same shape arrived on.
export interface IOrganizationMemoryExclusionsView {
  categories: string[];
}

// NOTE: NSAI-6559 — the icon a starter prompt is drawn with, for the
// personalized rows from `GET /user/memory/starter-prompts` and the static
// defaults alike. Mirrors the server's `EUserMemoryStarterPromptIcon` by hand;
// the server normalizes anything outside this set to IDEA, so a client never
// receives a value it cannot draw. Each app maps it to its own glyph.
export enum EUserMemoryStarterPromptIcon {
  MEETING = "meeting",
  CALENDAR = "calendar",
  EMAIL = "email",
  DOCUMENT = "document",
  IMPORT = "import",
  WRITE = "write",
  RESEARCH = "research",
  CODE = "code",
  DATA = "data",
  TASK = "task",
  IDEA = "idea",
}

export interface IUserMemoryStarterPrompt {
  text: string;
  icon: EUserMemoryStarterPromptIcon;
}

// NOTE: An empty `prompts` is the normal answer for a new user, memory turned
// off, or a generation still in flight — the client renders its static
// defaults and never has to tell those cases apart.
export interface IUserMemoryStarterPromptsView {
  prompts: IUserMemoryStarterPrompt[];
}
